import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useMutation } from 'react-query';
import { useHistory, Redirect } from 'react-router';
import Loader from '../../components/loader';
import PropTypes from 'prop-types';
import Filter from '../dashboard/filter';
import Gauge from '../dashboard/gauge';
import JobList from '../dashboard/joblist';
import { saveJob } from '../dashboard/service';
import { constructEncodedUrl } from '../../utils/helpers';
import { useSavedJobs } from '../../hooks/useSavedJobs';
import { useAppliedJobs } from '../../hooks/useAppliedJobs';
import { useJobSort } from '../../context/job-sort-context';

const SavedJobsList = (props) => {
  const { userInfo, contactedJobs } = props;

  if (userInfo?.user_type == 'recruiter') {
    return <Redirect to="/candidates" />;
  }

  const { appliedJobs } = useAppliedJobs();

  const history = useHistory();
  const [savingJobId, setSavingJobId] = useState(null);
  const [skip] = useState(history.location.state?.skip || 0);
  const [limit] = useState(5);
  const { sortBy } = useJobSort();

  const {
    jobs: savedJobs,
    isLoading: isLoadingSavedJobs,
    refetch: refetchSavedJobs,
    isFetching: isFetchingSavedJobs,
  } = useSavedJobs();
  useEffect(() => {
    /**
     * Doing this to get smooth transitions between saved/unsave and loading
     */
    if (!isFetchingSavedJobs) {
      if (savingJobId !== null) {
        setSavingJobId(null);
      }
    }
  }, [isFetchingSavedJobs]);

  const { mutate } = useMutation(saveJob, {
    onSettled: () => {
      refetchSavedJobs();
    },
    onMutate: (id) => setSavingJobId(id),
    useErrorBoundary: (error) => {
      return error.response?.status === 401;
    },
    retry: (count, error) => {
      if (error.response?.status === 401) {
        return false;
      } else if (count <= 3) {
        return true;
      } else {
        false;
      }
    },
  });

  const selectJob = (id, key, returnLink = false, params) => {
    const filters = {
      sortBy: sortBy.value,
      from: skip,
      size: limit,
      ...params,
    };
    if (returnLink) {
      return constructEncodedUrl({ id, filters }, 'job/saved');
    }
    history.push(constructEncodedUrl({ id, filters }, 'job/saved'));
  };

  const paginationArray = [
    ...Array(parseInt(Math.ceil(savedJobs?.length / limit) || 0)).keys(),
  ];

  const renderPagination = () => {
    let pages = paginationArray;

    if (pages.length > 5) {
      if (skip <= 2 * limit) {
        pages = pages.slice(0, 4).concat(['...', pages[pages.length - 1]]);
      } else if (skip > 2 * limit && skip < limit * (pages.length - 2)) {
        pages = [
          pages[0],
          '...',
          skip / limit - 1,
          skip / limit,
          skip / limit + 1,
          '...',
          pages[pages.length - 1],
        ];
      } else if (skip > limit * (pages.length - 3)) {
        pages = [
          pages[0],
          '...',
          pages[pages.length - 4],
          pages[pages.length - 3],
          pages[pages.length - 2],
          pages[pages.length - 1],
        ];
      }
    }

    return (
      <>
        {pages.length > 5 && (
          <button
            className="btn py-1 d-flex align-items-center justify-content-center"
            style={{ width: 'min-content' }}
            onClick={() => onPaginate(skip / limit - 1)}
          >
            <span className="chevron left"></span>
          </button>
        )}
        {pages.map((item, key) => {
          return (
            <button
              className={classNames(
                'btn px-3 py-1 d-flex align-items-center justify-content-center',
                {
                  'bg-primary text-white': item == skip / limit,
                  'cursor-default': item === '...',
                  'mr-2': key !== pages.length - 1,
                }
              )}
              key={key}
              onClick={() => (item !== '...' ? onPaginate(item) : null)}
            >
              <span className="d-block">{item !== '...' ? item + 1 : item}</span>
            </button>
          );
        })}
        {pages.length > 5 && (
          <button
            className="btn py-1 d-flex align-items-center justify-content-center"
            style={{ width: 'min-content' }}
            onClick={() => onPaginate(skip / limit + 1)}
          >
            <span className="chevron right"></span>
          </button>
        )}
      </>
    );
  };

  const onPaginate = (page) => {
    if (
      page == skip / limit ||
      page < 0 ||
      page > paginationArray[paginationArray.length - 1]
    ) {
      return;
    }
    window && window.scrollTo(0, 0);
    history.push('/jobs/saved', {
      skip: limit * page,
    });
  };

  React.useEffect(() => {
    if (savedJobs) {
      if (savedJobs?.length <= skip) {
        onPaginate((skip - limit) / 5);
      }
    }
  }, [savedJobs]);

  return (
    <>
      <div className="w-320px">
        <div className="row sticky-header">
          <div className="col-xs-12 col-md-12 col-xl-12">
            <div className="card sticky-header">
              <Filter view={false} onClearAll={() => onPaginate(0)} />
            </div>
          </div>
        </div>
      </div>
      <div className="w-75 cx-padding">
        <div className="row">
          <div className="col-xs-12 col-md-12 col-xl-12">
            <Gauge
              view="saved"
              appliedFilters={null}
              listLength={savedJobs && savedJobs?.length}
              showSearchaAndSortOptions={false}
            />

            {isLoadingSavedJobs ? (
              <Loader className="mt-3" />
            ) : (
              <>
                <JobList
                  data={savedJobs?.slice(skip, skip + limit)}
                  loading={isLoadingSavedJobs}
                  to={selectJob}
                  contactedJobs={contactedJobs}
                  appliedJobs={appliedJobs?.map((i) => i?.id)}
                  userInfo={userInfo}
                  showRemoveText={true}
                  saveJobFn={mutate}
                  savingJobId={savingJobId}
                  savedJobs={
                    (!isLoadingSavedJobs && savedJobs?.map((job) => job.id)) || []
                  }
                  onSelect={(id, key) => selectJob(id, key)}
                  candidateId={userInfo.id}
                />
                {savedJobs?.length == 0 ? (
                  <h3 className="text-center text-primary my-4">No Jobs</h3>
                ) : (
                  ''
                )}
                {savedJobs && (
                  <div className="pagination d-flex align-items-center justify-content-center mb-4">
                    {!(savedJobs?.length <= limit) && renderPagination()}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

SavedJobsList.propTypes = {
  userInfo: PropTypes.any,
  contactedJobs: PropTypes.any,
};

SavedJobsList.defaultProps = {
  userInfo: null,
};

export default SavedJobsList;
